'use client';

import Image from 'next/image';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

const SLACK_FALLBACK_ICON = '/assets/home/integrations/slack.svg';

/**
 * Workspace tile: renders the real Slack workspace icon (team.info) when
 * available, falling back to our branded Slack tile when it's missing or fails
 * to load. Base UI's Avatar shows the fallback automatically on load failure.
 */
function WorkspaceAvatar({ iconUrl }: { iconUrl: string | null }) {
  return (
    <Avatar className="size-10 shrink-0 rounded-lg">
      {iconUrl ? (
        <AvatarImage
          src={iconUrl}
          alt="Slack workspace"
          className="rounded-lg object-cover"
        />
      ) : null}
      <AvatarFallback className="rounded-lg bg-transparent p-0">
        <Image src={SLACK_FALLBACK_ICON} alt="Slack" width={40} height={40} />
      </AvatarFallback>
    </Avatar>
  );
}

interface SlackWorkspaceConnectionProps {
  isConfigured: boolean;
  workspaceName: string | null;
  workspaceIcon: string | null;
  isConnecting?: boolean;
  /** Fired for both the initial Connect and Reconnect actions. */
  onConnect: () => void;
  onDisconnect: () => void;
}

/**
 * Slack workspace connection card — shared between the Slack settings tab and
 * the Pulse/automations page so both render an identical connection surface.
 */
export default function SlackWorkspaceConnection({
  isConfigured,
  workspaceName,
  workspaceIcon,
  isConnecting = false,
  onConnect,
  onDisconnect,
}: SlackWorkspaceConnectionProps) {
  return (
    <div className="rounded-lg p-6 border bg-ds-surface-white border-ds-border-subtlest">
      <h3 className="font-medium mb-4 text-sm text-ds-text-darker">
        Workspace Connection
      </h3>
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <WorkspaceAvatar iconUrl={workspaceIcon} />
          <div className="min-w-0">
            {isConfigured ? (
              <>
                <p className="ds-text-body-md-semibold truncate text-ds-text-darker">
                  {workspaceName || 'Unknown Workspace'}
                </p>
                <div
                  className="mt-1 flex items-center gap-1.5"
                  role="status"
                  aria-label="Connected"
                >
                  <span
                    className="size-1.5 shrink-0 rounded-full bg-ds-brand-default"
                    aria-hidden
                  />
                  <span className="ds-text-body-xs-medium leading-none text-ds-text-subtle">
                    Connected
                  </span>
                </div>
              </>
            ) : (
              <>
                <p className="ds-text-body-md-semibold text-ds-text-darker">
                  Slack workspace
                </p>
                <p className="ds-text-body-xs-medium mt-0.5 text-ds-text-subtle">
                  Not connected
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {isConfigured ? (
            <>
              <Button variant="outline" size="sm" onClick={onConnect}>
                Reconnect
              </Button>
              <Button
                variant="destructiveOutline"
                size="sm"
                onClick={onDisconnect}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onConnect} disabled={isConnecting}>
              {isConnecting ? 'Connecting…' : 'Connect to Slack'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
